"use client";
import { useEffect, useState } from "react";
import Table from "@/components/tables/Table";
import Button from "./Buttons/Button";
import { fetchPollenAnalyticsByPage } from "@/util/app.util";
import { formatTimestamp } from "@/util/helper";
import {
  aqColumns1,
  AQHeaders1,
  pollenColumns1,
  PollenHeaders1,
} from "@/constants/constants";
import { formatData } from "@/util/renderHelper";
import { useMediaQuery } from "@react-hook/media-query";
import Link from "next/link";
import { usePathname } from "next/navigation";

interface LevelBodyProps {
  type: string;
  level: "best" | "worst";
  country: string;
}

function LevelBody({
  type,
  level,
  country,
}: LevelBodyProps): React.ReactElement {
  const IS_MOBILE_VIEW = useMediaQuery("(max-width: 1000px)");
  const [tableData, setTableData] = useState<any>({
    headers: type == "aq" ? AQHeaders1 : PollenHeaders1,
    rows: [],
  });
  const [formattedTime, setFormattedTime] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const pathname = usePathname();
  const [activeLevel, setActiveLevel] = useState<"best" | "worst">(level);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const response = await fetchPollenAnalyticsByPage(
        type,
        level,
        page,
        country
      );
      const combinedData = [...(response.data || [])];
      type == "pollen" &&
        combinedData.sort((a, b) => {
          const [maxKeyA] = Object.entries(
            a.Count
          ).reduce((max: any, entry: any) => (entry[1] > max[1] ? entry : max));

          if (activeLevel === "worst") {
            return (b.Count[maxKeyA] || 0) - (a.Count[maxKeyA] || 0); // Sort by highest value
          } else if (activeLevel === "best") {
            return (a.Count[maxKeyA] || 0) - (b.Count[maxKeyA] || 0); // Sort by lowest value
          }
          return 0;
        });
      setHasNextPage(response.hasNextPage);
      const updatedAt: any = response.updatedAt ?? "";
      setFormattedTime(formatTimestamp(updatedAt));
      const formattedData = formatData(type, combinedData, 1, country);
      if (formattedData?.rows?.length > 0) {
        setTableData(formattedData);
      }
    };

    loadData();
    setLoading(false);
  }, [level, country, type]);

  const loadMoreData = async () => {
    if (!hasNextPage) return;
    const nextPage = page + 1;
    setLoading(true);
    // Fetch data for both USA and UK for the next page
    const response = await fetchPollenAnalyticsByPage(
      type,
      level,
      nextPage,
      country
    );
    const combinedData = [...(response.data || [])];

    if (combinedData.length > 0) {
      const modifiedData = formatData(type, combinedData, nextPage, country);
      const combinedRows = [...tableData.rows, ...modifiedData.rows];
      setTableData({
        headers: tableData.headers,
        rows: combinedRows,
      });
      setPage(nextPage);
    }

    // Determine if there is a next page
    setHasNextPage(response.hasNextPage);
    setLoading(false);
  };

  useEffect(() => {
    if (
      pathname.includes("least-polluted") ||
      pathname.includes("least-pollen")
    ) {
      setActiveLevel("best");
    } else {
      setActiveLevel("worst");
    }
  }, [pathname]);

  const newLevel = activeLevel === "best" ? "worst" : "best";

  const label =
    newLevel === "worst"
      ? type === "aq"
        ? "Most Polluted"
        : "Worst Pollen"
      : type === "aq"
      ? "Least Polluted"
      : "Low Allergy";

  const href =
    newLevel === "best"
      ? type === "aq"
        ? `/${country}/least-polluted-cities`
        : `/${country}/cities-with-least-pollen`
      : type === "aq"
      ? `/${country}/most-polluted-cities`
      : `/${country}/cities-with-worst-pollen`;
  return (
    <section className="">
      {IS_MOBILE_VIEW ? (
        <div className="flex flex-col w-fit gap-4 pb-8">
          <Link
            href={href}
            onClick={() => setActiveLevel(newLevel)}
            className="hover:text-[#1EDCD3] appearance-none bg-transparent text-secondary-100 text-md font-poppinsMedium"
          >
            See {label} Cities
          </Link>
          <a
            key="scale"
            className="hover:text-[#1EDCD3] appearance-none bg-transparent text-secondary-100  text-md font-poppinsMedium"
            href="#scale"
          >
            Scroll to Scale
          </a>
        </div>
      ) : (
        ""
      )}
      <div className="flex flex-col lg:flex-row lg:p-0 lg:space-x-16 justify-between items-start lg:pb-4">
        <div className="w-full">
          <h2 className=" font-poppinsRegular font-semibold tracking-wide whitespace-pre-wrap lg:whitespace-nowrap text-neutral-light-50">
            Current {type == "aq" ? "air quality" : "pollen"} rankings:
            <span className="text-primary-50 leading-normal">
              {activeLevel == "worst" ? " Worst allergy " : " Low allergy "}
              cities in the {country.toUpperCase()}
            </span>
          </h2>
        </div>
      </div>
      <div className="flex justify-between py-8 items-center">
        <p className="text-neutral-light-900 text-sm lg:text-base">
          Last updated - {formattedTime} (Local time)
        </p>

        {!IS_MOBILE_VIEW ? (
          <div className="flex justify-end whitespace-nowrap gap-6">
            <a
              key="scale"
              className="hover:text-[#1EDCD3] appearance-none bg-transparent text-secondary-100 h-full w-full text-md font-poppinsMedium"
              href="#scale"
            >
              Scroll to Scale
            </a>
            <Link
              href={href}
              onClick={() => setActiveLevel(newLevel)}
              className="hover:text-[#1EDCD3] appearance-none bg-transparent text-secondary-100 h-full w-full text-md font-poppinsMedium"
            >
              See {label} Cities
            </Link>
          </div>
        ) : (
          ""
        )}
      </div>
      <div className="w-full items-center overflow-auto pb-8 max-h-[55rem] lg:max-h-[120rem]">
        <Table
          columns={type == "aq" ? aqColumns1 : pollenColumns1}
          data={tableData.rows}
          type="level"
        />
      </div>
      {loading && <div className="text-neutral-500">Loading...</div>}
      {hasNextPage && !loading && (
        <Button
          text="Load More"
          style="p-0 -mx-8 w-fit font-poppinsRegular text-secondary-100 hover:text-[#1EDCD3] shadow-none"
          handleClick={loadMoreData}
        />
      )}
    </section>
  );
}

export default LevelBody;
